// Page-held visibility over the shared perception model. Unknown inputs stay unknown.
import {
  collectLightAndDarkSources, fixtureLightSources, hasStatus, lightContext, PERCEPTION_STATUS, position, positionKnown, tremorsenseReaches,
  sensedByViewer, withinReach, type PerceptionShape,
} from '../../encounter-map/ft3d/perception.js';
import { encounterCreatureField, posCoord } from '../../shared/array-types.js';
import { pagePerceptionContext } from '../geometry/perception-context.js';
import { encounterShapeForReaders } from '../geometry/encounter-shape.js';
import { type Row, inventoryId, isInventoryRow, record } from './context.js';

/** MV4 (owner-authorized, 2026-09-28): the FOURTH state `position_known` — a Burrowed target (ruling 6): its circle is public, but
 *  neither sight nor touch targets it; only a tremorsense viewer in range reads it as `visible` (the enclosing solid's `blocked`
 *  cover is then the tremorsense clause's to judge). Every consumer treats it like `unseen`, never like `touch_located`. */
export type VisibilityState = 'visible' | 'touch_located' | 'position_known' | 'unseen' | null;

export function abilityVisibility(encounter: Row, viewer: Row, target: Row): VisibilityState {
  if (!Array.isArray(encounter.encounter_creatures_arr) || !Array.isArray(encounter.encounter_inventories_arr)) return null;
  const candidate = encounterShapeForReaders(encounter);
  if (!candidate || !Array.isArray(candidate.boxes)
      || (candidate.openings != null && !Array.isArray(candidate.openings))) return null;
  // build_pos_arr() (janus/core/build_v_arr.php) surfaces only NON-ZERO axes and omits `position` entirely when all are zero; its
  // contract is that an absent axis IS 0, which the shared readers already apply.
  if (!record(viewer.encounter_creatures)) return null;
  const memberId = (row: unknown): string => String(encounterCreatureField(record(row)?.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase();
  const members = new Set(encounter.encounter_creatures_arr.map(memberId).filter(Boolean));
  if (!members.has(memberId(viewer))) return null;
  if (isInventoryRow(target)) {
    if (!encounter.encounter_inventories_arr.some(row => inventoryId(record(row)) === inventoryId(target))) return null;
  } else if (!record(target.encounter_creatures) || !members.has(memberId(target))) return null;
  // In a held FT3D shape, omitted depth is zero, not the legacy 2D `position().y === null` lane.
  const located = (row: Row): Row => {
    const at = { x_pos: posCoord(row, 'x_pos') ?? 0, y_pos: posCoord(row, 'y_pos') ?? 0, z_pos: posCoord(row, 'z_pos') ?? 0 };
    // Perception reads inventory columns before `position`; use the same compiled coordinates as object attack geometry.
    return { ...row, ...(isInventoryRow(row) ? at : {}), position: { ...record(row.position), ...at } };
  };
  viewer = located(viewer); target = located(target);
  // Owner ruling 6: the Burrowed circle is public; light cannot make its position unknown.
  if (positionKnown(target)) return tremorsenseReaches(viewer, target) ? 'visible' : 'position_known';
  const geometry = candidate as unknown as PerceptionShape;
  try {
    const seed = pagePerceptionContext(encounter, geometry, [position(viewer), position(target)]);
    if (!seed) return null;
    const sources = collectLightAndDarkSources(encounter);
    const lightSources = [...sources.lightSources, ...fixtureLightSources(geometry, seed.fixtureLightRadius)];
    const context = lightContext(encounter, geometry, seed);
    if (sensedByViewer(encounter, geometry, viewer, target, lightSources, sources.darkSources,
      sources.obscuredSources, { context, allowWithinReach: false })) return 'visible';
    if (!hasStatus(target, PERCEPTION_STATUS.hidden)
      && !hasStatus(target, PERCEPTION_STATUS.invisible, PERCEPTION_STATUS.greaterInvisible)
      && !hasStatus(target, PERCEPTION_STATUS.ethereal)
      && withinReach(viewer, target)) return 'touch_located';
    return 'unseen';
  } catch { return null; }
}
